import { IdcardOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Avatar, Button, DatePicker, Form, Input, Modal, Pagination, Spin, Table } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
	DataTableCard,
	EmptyState,
	PageHeader,
	RowActions,
	SearchInput,
} from "@/shared/components/ui";
import { useTableState } from "@/shared/hooks/useTableState";
import { isConflictError } from "@/shared/lib/api-error";
import { formatDate, orDash } from "@/shared/lib/format";
import {
	useCreatePerson,
	useDeletePerson,
	usePersonsList,
	useUpdatePerson,
} from "../hooks/usePersons";
import type { Person, PersonCreateRequest } from "../types";

interface PersonFormValues extends Omit<PersonCreateRequest, "birthDate"> {
	birthDate?: Dayjs;
}

function initials(fullName: string) {
	return (
		fullName
			.trim()
			.split(/\s+/)
			.slice(0, 2)
			.map((part) => part[0]?.toUpperCase())
			.join("") || "?"
	);
}

interface PersonsMobileListProps {
	persons: Person[];
	isLoading: boolean;
	search: string;
	total: number;
	page: number;
	limit: number;
	onPageChange: (page: number, pageSize: number) => void;
	openCreate: () => void;
	openEdit: (person: Person) => void;
	onDelete: (id: string) => void;
	deleting: boolean;
}

function PersonsMobileList({
	persons,
	isLoading,
	search,
	total,
	page,
	limit,
	onPageChange,
	openCreate,
	openEdit,
	onDelete,
	deleting,
}: PersonsMobileListProps) {
	if (isLoading) {
		return (
			<div className="flex justify-center p-8">
				<Spin />
			</div>
		);
	}

	if (persons.length === 0) {
		return (
			<div className="p-4">
				<EmptyState
					title={search ? "Hech narsa topilmadi" : "Shaxslar yo'q"}
					description={
						search
							? "Boshqa ism bilan qidirib ko'ring."
							: "Yordam oluvchilar va xodimlarni shu yerga qo'shing."
					}
					icon={<IdcardOutlined />}
					action={
						search ? null : (
							<Button icon={<PlusOutlined />} onClick={openCreate}>
								Shaxs qo'shish
							</Button>
						)
					}
				/>
			</div>
		);
	}

	return (
		<div>
			<div className="divide-y divide-[var(--c-border-subtle)]">
				{persons.map((person) => (
					<div key={person.id} className="flex items-center gap-3 p-3.5">
						<Avatar
							size={34}
							className="shrink-0 bg-[var(--c-hover)] text-xs font-medium text-[var(--c-text-muted)]"
						>
							{initials(person.fullName)}
						</Avatar>

						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-medium text-[var(--c-text)]">
								{person.fullName}
							</div>
							<div className="numeric mt-0.5 truncate text-xs text-[var(--c-text-muted)]">
								{orDash(person.phone)}
								{person.passportNumber ? ` · ${person.passportNumber}` : ""}
							</div>
						</div>

						<RowActions
							alwaysVisible
							onEdit={() => openEdit(person)}
							onDelete={() => onDelete(person.id)}
							deleteTitle="Shaxs o'chirilsinmi?"
							deleting={deleting}
						/>
					</div>
				))}
			</div>

			{total > limit && (
				<div className="flex justify-center border-t border-[var(--c-border-subtle)] p-3">
					<Pagination
						simple
						current={page}
						pageSize={limit}
						total={total}
						onChange={onPageChange}
					/>
				</div>
			)}
		</div>
	);
}

export function PersonsPage() {
	const { message } = App.useApp();
	const { page, limit, search, onSearch, onPageChange } = useTableState();
	const { data, isLoading, isFetching } = usePersonsList({
		page,
		limit,
		fullName: search || undefined,
	});

	const [form] = Form.useForm<PersonFormValues>();
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState<Person | null>(null);

	const createMutation = useCreatePerson();
	const updateMutation = useUpdatePerson();
	const deleteMutation = useDeletePerson();

	useEffect(() => {
		if (modalOpen) {
			form.setFieldsValue({
				fullName: editing?.fullName ?? "",
				phone: editing?.phone ?? undefined,
				passportNumber: editing?.passportNumber ?? undefined,
				pinfl: editing?.pinfl ?? undefined,
				birthDate: editing?.birthDate ? dayjs(editing.birthDate) : undefined,
			});
		}
	}, [modalOpen, editing, form]);

	const openCreate = () => {
		setEditing(null);
		setModalOpen(true);
	};

	const openEdit = (person: Person) => {
		setEditing(person);
		setModalOpen(true);
	};

	const handleSubmit = async () => {
		const values = await form.validateFields();
		const payload = {
			...values,
			birthDate: values.birthDate ? values.birthDate.format("YYYY-MM-DD") : undefined,
		};

		if (editing) {
			updateMutation.mutate(
				{ id: editing.id, data: payload },
				{ onSuccess: () => setModalOpen(false) }
			);
		} else {
			createMutation.mutate(payload, { onSuccess: () => setModalOpen(false) });
		}
	};

	const handleDelete = (id: string) => {
		deleteMutation.mutate(id, {
			onError: (error) => {
				message.error(
					isConflictError(error)
						? "O'chirib bo'lmadi: shaxs ishlatilmoqda"
						: "O'chirishda xatolik yuz berdi"
				);
			},
		});
	};

	const total = data?.meta.total ?? 0;
	const persons = data?.items ?? [];
	const isDataLoading = isLoading || isFetching;

	return (
		<div>
			<PageHeader
				title="Shaxslar"
				description="Yordam oluvchilar va xodimlar bazasi"
				actions={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={openCreate}
						className="w-full sm:w-auto"
					>
						Shaxs qo'shish
					</Button>
				}
			/>

			<DataTableCard
				toolbar={
					<SearchInput
						placeholder="F.I.Sh. bo'yicha qidirish…"
						onSearch={onSearch}
						className="w-full sm:w-72"
					/>
				}
				toolbarExtra={total ? `${total} ta yozuv` : null}
			>
				{/* Desktop table */}
				<div className="hidden sm:block">
					<Table<Person>
						rowKey="id"
						loading={isDataLoading}
						dataSource={persons}
						scroll={{ x: "max-content" }}
						locale={{
							emptyText: (
								<EmptyState
									title={search ? "Hech narsa topilmadi" : "Shaxslar yo'q"}
									description={
										search
											? "Boshqa ism bilan qidirib ko'ring."
											: "Yordam oluvchilar va xodimlarni shu yerga qo'shing."
									}
									icon={<IdcardOutlined />}
									action={
										search ? null : (
											<Button icon={<PlusOutlined />} onClick={openCreate}>
												Shaxs qo'shish
											</Button>
										)
									}
								/>
							),
						}}
						pagination={
							total > limit
								? {
										current: page,
										pageSize: limit,
										total,
										showSizeChanger: true,
										onChange: onPageChange,
										className: "!px-4",
									}
								: false
						}
						columns={[
							{
								title: "F.I.Sh.",
								dataIndex: "fullName",
								render: (value: string) => (
									<div className="flex items-center gap-2.5">
										<Avatar
											size={26}
											className="shrink-0 bg-[var(--c-hover)] text-[11px] font-medium text-[var(--c-text-muted)]"
										>
											{initials(value)}
										</Avatar>
										<span className="font-normal text-[var(--c-text)]">{value}</span>
									</div>
								),
							},
							{
								title: "Telefon",
								dataIndex: "phone",
								width: 160,
								render: (value: string | null) => <span className="numeric">{orDash(value)}</span>,
							},
							{
								title: "Passport",
								dataIndex: "passportNumber",
								width: 140,
								responsive: ["md"],
								render: (value: string | null) => (
									<span className="numeric text-[var(--c-text-secondary)]">{orDash(value)}</span>
								),
							},
							{
								title: "JSHSHIR",
								dataIndex: "pinfl",
								width: 170,
								responsive: ["lg"],
								render: (value: string | null) => (
									<span className="numeric text-[var(--c-text-secondary)]">{orDash(value)}</span>
								),
							},
							{
								title: "Tug'ilgan sana",
								dataIndex: "birthDate",
								width: 130,
								responsive: ["lg"],
								render: (value: string | null) => (
									<span className="numeric text-[var(--c-text-secondary)]">
										{formatDate(value)}
									</span>
								),
							},
							{
								title: "",
								key: "actions",
								width: 88,
								align: "right",
								fixed: "right",
								render: (_, record) => (
									<RowActions
										onEdit={() => openEdit(record)}
										onDelete={() => handleDelete(record.id)}
										deleteTitle="Shaxs o'chirilsinmi?"
										deleting={deleteMutation.isPending}
									/>
								),
							},
						]}
					/>
				</div>

				{/* Mobile cards */}
				<div className="block sm:hidden">
					<PersonsMobileList
						persons={persons}
						isLoading={isDataLoading}
						search={search}
						total={total}
						page={page}
						limit={limit}
						onPageChange={onPageChange}
						openCreate={openCreate}
						openEdit={openEdit}
						onDelete={handleDelete}
						deleting={deleteMutation.isPending}
					/>
				</div>
			</DataTableCard>

			<Modal
				title={editing ? "Shaxsni tahrirlash" : "Yangi shaxs"}
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				onOk={handleSubmit}
				okText={editing ? "Saqlash" : "Qo'shish"}
				cancelText="Bekor qilish"
				confirmLoading={createMutation.isPending || updateMutation.isPending}
				destroyOnHidden
				width={560}
			>
				<Form form={form} layout="vertical" className="mt-4" requiredMark={false}>
					<Form.Item
						name="fullName"
						label="F.I.Sh."
						rules={[{ required: true, message: "F.I.Sh. kiritilishi shart" }]}
					>
						<Input placeholder="masalan: Aliyev Vali Aliyevich" autoFocus />
					</Form.Item>

					<div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
						<Form.Item name="phone" label="Telefon">
							<Input placeholder="+998901234567" maxLength={20} className="numeric" />
						</Form.Item>
						<Form.Item name="passportNumber" label="Passport raqami">
							<Input placeholder="AA1234567" maxLength={20} className="numeric" />
						</Form.Item>
						<Form.Item name="birthDate" label="Tug'ilgan sana" className="sm:!mb-0">
							<DatePicker
								format="DD.MM.YYYY"
								className="w-full"
								placeholder="Sanani tanlang"
								allowClear={false}
							/>
						</Form.Item>
						<Form.Item
							name="pinfl"
							label="JSHSHIR (PINFL)"
							rules={[
								{
									// PINFL is optional, so only validate the length once something is typed.
									validator: (_, value: string | undefined) =>
										!value || value.length === 14
											? Promise.resolve()
											: Promise.reject(new Error("JSHSHIR 14 raqamdan iborat bo'lishi kerak")),
								},
							]}
							className="!mb-0"
						>
							<Input
								placeholder="12345678901234"
								maxLength={14}
								inputMode="numeric"
								className="numeric"
							/>
						</Form.Item>
					</div>
				</Form>
			</Modal>
		</div>
	);
}
